import { ValidationError } from '~/errors';
import { leasingServiceUrl, settingsServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { normalizePhoneForBackend, backendPhoneRegex } from '~/utils/validations';
import { BaseRestRepository } from './baseRestRepository';
import type { CreateRentalApplicationDto, RentalApplicationListDtoPaginationDto } from '~/swagger/leasing/Api';
import type { HydratedTenantDto } from '~/swagger/portal/Api';
import type { RentalApplicationGeneralSettings } from '~/swagger/settings/Api';
import type { GeneralQuestion, GetRentalApplicationsParams, IRentalApplicationDto, QuestionCategories } from '~/types/generalQuestions';

const RENTAL_APPLICATION_URL = '/api/rental-applications';
const leasingAppUrl = (path: string) => `${leasingServiceUrl()}/company-portal/rental-applications${path}`;
export function validateId(id: string, entityName = 'RentalApplication'): void {
  if (!id) {
    throw new ValidationError(`${entityName} ID must be provided`);
  }
}
export class RentalApplicationRepository extends BaseRestRepository {
  public async getApplications(filter?: GetRentalApplicationsParams) {
    const url = urlWithQuery(leasingAppUrl(''), filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getApplication(applicationId: string): Promise<IRentalApplicationDto> {
    const url = leasingAppUrl(`/${applicationId}`);
    const response = await this.fetchWithAuth(url);
    const json = await this.getJsonResponse(response);
    return {
      ...json,
      applicationQuestions: this.generateQuestionMap(json.applicationQuestions),
    };
  }

  public async updateApplication(applicationId: string, body: Partial<CreateRentalApplicationDto>) {
    const url = leasingAppUrl(`/${applicationId}`);
    const normalizedBody = this.normalizePhonesInPayload(body);
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(normalizedBody),
    });
    const json = await this.getJsonResponse(response);
    if (json.applicationQuestions && typeof json.applicationQuestions === 'object' && !Array.isArray(json.applicationQuestions)) {
      const questionsMap = new Map<QuestionCategories, GeneralQuestion[]>();
      Object.entries(json.applicationQuestions).forEach(([category, questions]) => {
        if (Array.isArray(questions)) {
          questionsMap.set(category as QuestionCategories, this.assignUniqueIds(questions));
        }
      });
      return {
        ...json,
        applicationQuestions: questionsMap,
      };
    }
    return json;
  }

  public async createApplication(body: CreateRentalApplicationDto): Promise<IRentalApplicationDto> {
    const url = leasingAppUrl('');
    const normalizedBody = this.normalizePhonesInPayload(body);
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(normalizedBody),
    });
    const json = await this.getJsonResponse(response);
    return {
      ...json,
      applicationQuestions: this.generateQuestionMap(json.applicationQuestions),
    };
  }

  public async getApplicationSettings(): Promise<RentalApplicationGeneralSettings> {
    const url = `${settingsServiceUrl()}/company-portal/settings/rentalapplicationgeneral/company`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getRentalApplications(filter?: GetRentalApplicationsParams): Promise<RentalApplicationListDtoPaginationDto> {
    const url = urlWithQuery(RENTAL_APPLICATION_URL, filter);
    const response = await this.fetchWithAuth(url);
    return await this.getJsonResponse(response);
  }

  public async getRentalApplication(id: string): Promise<IRentalApplicationDto> {
    validateId(id);
    const url = leasingAppUrl(`/${id}`);
    const response = await this.fetchWithAuth(url);
    const json = await this.getJsonResponse(response);
    return {
      ...json,
      applicationQuestions: this.generateQuestionMap(json.applicationQuestions),
    };
  }

  public async deleteRentalApplication(id: string): Promise<void> {
    validateId(id);
    if (!id) {
      throw new ValidationError('RentalApplication ID must be provided');
    }
    const url = leasingAppUrl(`/${id}`);
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async tenantConvert(rentalApplicationId: string): Promise<HydratedTenantDto> {
    const url = leasingAppUrl(`/${rentalApplicationId}/tenant-convert`);
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }

  private normalizePhonesInPayload<T>(payload: T): T {
    const normalize = (value: any): any => {
      if (Array.isArray(value)) {
        return value.map(normalize);
      }
      if (value && typeof value === 'object') {
        const result: any = {};
        for (const [key, val] of Object.entries(value)) {
          if (typeof val === 'string' && /phone/i.test(key)) {
            if (backendPhoneRegex.test(val)) {
              result[key] = val;
            } else {
              const normalized = normalizePhoneForBackend(val);
              result[key] = normalized ?? val;
            }
          } else {
            result[key] = normalize(val);
          }
        }
        return result;
      }
      return value;
    };
    return normalize(payload);
  }

  private generateQuestionMap(questions: any) {
    const questionsMap = new Map<QuestionCategories, GeneralQuestion[]>();
    if (!questions) return questionsMap;
    Object.entries(questions).forEach(([category, questions]) => {
      if (Array.isArray(questions)) {
        questionsMap.set(category as QuestionCategories, this.assignUniqueIds(questions));
      }
    });
    return questionsMap;
  }
  private assignUniqueIds(questions: GeneralQuestion[]): GeneralQuestion[] {
    return questions.map((question) => {
      const questionId = question.id || crypto.randomUUID();
      const answersWithIds = (question.answers || []).map((answer) => {
        const answerId = answer.id || crypto.randomUUID();
        const followUpQuestionsWithIds = (answer.followUpQuestions || []).map((followUpQuestion) => {
          const followUpQuestionId = followUpQuestion.id || crypto.randomUUID();
          const followUpAnswersWithIds = (followUpQuestion.answers || []).map((followUpAnswer) => ({
            ...followUpAnswer,
            id: followUpAnswer.id || crypto.randomUUID(),
          }));
          return {
            ...followUpQuestion,
            id: followUpQuestionId,
            answers: followUpAnswersWithIds,
          };
        });
        return {
          ...answer,
          id: answerId,
          followUpQuestions: followUpQuestionsWithIds,
        };
      });
      return {
        ...question,
        id: questionId,
        answers: answersWithIds,
      };
    });
  }

  private normalizeIsFreeFormInput(questions: any[]): GeneralQuestion[] {
    return (questions || []).map((q: any) => ({
      ...q,
      answers: (q.answers || []).map((a: any) => ({
        ...a,
        isFreeFormInput: a?.isFreeFormInput ?? a?.isFreeformInput ?? false,
        followUpQuestions: this.normalizeIsFreeFormInput(a?.followUpQuestions || []),
      })),
    }));
  }
}

export const rentalApplicationRepo = new RentalApplicationRepository();
